import React, { useEffect, useState } from 'react';
import { api } from '../../shared/api';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };

type Branch = { id: number; name: string; region: string | null };
type Node = {
    id: number; name: string; position: string | null; department: string | null;
    status: string | null; branches: Branch[]; children: Node[];
};
type OrgResp = { tree: Node[]; stats: { employees: number; managed_branches: number; unassigned_branches: number } };

export function OrgTab({ token, locale, onSelect }: { token: string; locale: Locale; user: User; onSelect?: (employeeId: number) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<OrgResp | null>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        api<OrgResp>('/hr/org', token).then(setData).catch((e) => setErr(String(e?.message ?? e)));
    }, [token]);

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Organigram', 'Org chart')}</strong>
                {data ? (
                    <div className="muted" style={{ fontSize: '0.8rem', marginTop: 6 }}>
                        {data.stats.employees} {t('zaposlenika', 'employees')} · {data.stats.managed_branches} {t('vođenih poslovnica', 'managed branches')}
                        {data.stats.unassigned_branches > 0 ? ` · ${data.stats.unassigned_branches} ${t('bez voditelja', 'unassigned')}` : ''}
                    </div>
                ) : null}
            </div>
            <div style={card}>
                {err ? <div style={{ color: '#b14040', fontSize: '0.82rem' }}>⚠ {err}</div> : null}
                {!data && !err ? <div className="muted">{t('Učitavam…', 'Loading…')}</div> : null}
                {data && data.tree.length === 0 ? <div className="muted">{t('Hijerarhija još nije postavljena (pokreni bi:hr-seed-hierarchy).', 'Hierarchy not set yet (run bi:hr-seed-hierarchy).')}</div> : null}
                {data ? <div style={{ display: 'grid', gap: 6 }}>{data.tree.map((n) => <OrgNode key={n.id} node={n} depth={0} locale={locale} onSelect={onSelect} />)}</div> : null}
            </div>
        </div>
    );
}

function OrgNode({ node, depth, locale, onSelect }: { node: Node; depth: number; locale: Locale; onSelect?: (employeeId: number) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [open, setOpen] = useState(depth < 2);
    const hasChildren = node.children.length > 0;

    return (
        <div>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                marginLeft: depth * 22, borderRadius: 8, background: depth === 0 ? '#faf6ec' : '#fff',
                border: '1px solid ' + (depth === 0 ? GOLD : '#f0e8d6'),
            }}>
                {hasChildren ? (
                    <button type="button" onClick={() => setOpen(!open)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: BROWN, fontWeight: 700, width: 16 }}>{open ? '▾' : '▸'}</button>
                ) : <span style={{ width: 16 }} />}
                <span style={{ fontWeight: 700, color: BROWN, cursor: onSelect ? 'pointer' : 'default', textDecoration: onSelect ? 'underline dotted' : 'none' }} onClick={() => onSelect?.(node.id)} title={onSelect ? t('Otvori dosje', 'Open dossier') : undefined}>{node.name}</span>
                {node.position ? <span style={{ fontSize: '0.78rem', color: '#8a7a55' }}>· {node.position}</span> : null}
                {node.status && node.status !== 'active' ? <span style={{ fontSize: '0.72rem', color: '#b14040' }}>({node.status})</span> : null}
                {node.branches.length > 0 ? (
                    <span style={{ fontSize: '0.72rem', color: '#fff', background: GOLD, borderRadius: 999, padding: '1px 8px', marginLeft: 4 }}>
                        {node.branches.length} {t('posl.', 'br.')}
                    </span>
                ) : null}
            </div>
            {open && node.branches.length > 0 ? (
                <div style={{ marginLeft: depth * 22 + 30, fontSize: '0.78rem', color: '#8a7a55', padding: '2px 0' }}>
                    {node.branches.map((b) => b.name).join(' · ')}
                </div>
            ) : null}
            {open && hasChildren ? (
                <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                    {node.children.map((c) => <OrgNode key={c.id} node={c} depth={depth + 1} locale={locale} onSelect={onSelect} />)}
                </div>
            ) : null}
        </div>
    );
}
